// Mirrors inrange_agents.agents.procurement_findings + procurement_pricing
// on the BE. The Procurement tab renders these directly.

import type { Sighting } from "../hooks/useUploadsList";

export type PricingRag = "green" | "amber" | "red" | "unknown";

export interface PricingAssessment {
  raw_total_pounds: number | null;
  raw_per_kwp: number | null;
  adjusted_total_pounds: number | null;
  adjusted_per_kwp: number | null;
  excluded_lines: Array<{
    section: string | null;
    label: string | null;
    total_cost: number;
  }>;
  target_per_kwp: number | null;
  rag: PricingRag;
  target_country: string | null;
  notes: string[];
}

export interface FollowupQuestion {
  question: string;
  why_asked: string;
  priority: "high" | "medium" | "low";
  cited_cost_rows: string[];
  cited_survey_paths: string[];
}

export interface CostRisk {
  area: string;
  scenario: string;
  estimated_impact_pounds: number | null;
  confidence: number;
  cited_cost_rows: string[];
  cited_survey_paths: string[];
}

export interface CostOpportunity {
  area: string;
  how: string;
  estimated_saving_pounds: number | null;
  confidence: number;
  cited_cost_rows: string[];
  cited_survey_paths: string[];
}

export type ProcurementAlignmentStatus =
  | "aligned"
  | "over_costed"
  | "under_costed_or_missing"
  | "informational";

export interface AlignmentItem {
  label: string;
  status: ProcurementAlignmentStatus;
  rationale: string;
  confidence: number;
  cited_cost_rows: string[];
  cited_survey_paths: string[];
}

export type BenchmarkCohortKind = "epc" | "tenant_landlord" | "size_band" | "global";

export interface CohortMember {
  project_id: string;
  name: string | null;
}

export interface BenchmarkCohort {
  cohort_kind: BenchmarkCohortKind;
  discriminator: string | null;
  n: number;
  p25: number;
  p50: number;
  p75: number;
  mean: number;
  // The past projects whose adjusted £/kWp values populated this
  // cohort. Surfaced on the procurement tab as a small list under each
  // row so the operator can see which assessments the percentiles
  // come from. Optional because persisted assessments produced before
  // this field shipped don't have it; the GET endpoint live-recomputes
  // and overlays it, but other endpoints (e.g. the dismissal route)
  // return the persisted findings as-is.
  members?: CohortMember[];
}

export interface BenchmarkRollup {
  currency_code: string | null;
  cohorts: BenchmarkCohort[];
  skipped: Array<{ cohort_kind: string; reason: string }>;
}

// Cross-site procurement comparison. Mirrors
// inrange_agents.agents.procurement_cross_site.CrossSiteResult; the
// FE renders the deterministic tables (headlines / category rollup /
// per-line matrix) and the LLM's structured analysis side by side.
export interface CrossSiteHeadline {
  project_id: string;
  name: string;
  size_kwp: number | null;
  total_pounds: number | null;
  adjusted_per_kwp: number | null;
  rank: number;
  pct_vs_cheapest: number | null;
}

export interface CrossSiteCategoryCell {
  project_id: string;
  category: string;
  per_kwp: number | null;
}

export interface CrossSitePerLineCell {
  project_id: string;
  line_label: string;
  per_kwp: number | null;
}

export interface CrossSiteSurveySnapshot {
  project_id: string;
  text: string;
}

export interface CrossSiteFindingsSnapshot {
  project_id: string;
  pricing_explanation: string;
  epc_followup_count: number;
  cost_risk_count: number;
  cost_opportunity_count: number;
}

export interface CrossSiteRecommendation {
  priority: "high" | "medium" | "low";
  area: string;
  recommendation: string;
  why: string;
}

export interface CrossSiteCallout {
  project_id: string;
  callout: string;
}

export interface CrossSiteComparison {
  headline: string;
  key_insights: string[];
  recommendations: CrossSiteRecommendation[];
  per_site_callouts: CrossSiteCallout[];
}

export interface CrossSiteResult {
  project_ids: string[];
  // The shared currency of the compared projects (GBP / EUR, or null when
  // unlinked). The comparison is single-currency by construction (mixed
  // selections are blocked), so every figure renders with this symbol.
  currency_code: string | null;
  headlines: CrossSiteHeadline[];
  category_rollup: CrossSiteCategoryCell[];
  per_line_matrix: CrossSitePerLineCell[];
  survey_snapshots: CrossSiteSurveySnapshot[];
  findings_snapshots: CrossSiteFindingsSnapshot[];
  comparison: CrossSiteComparison;
  model_id: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
}

export interface ProcurementFindings {
  pricing_assessment: PricingAssessment;
  pricing_explanation: string;
  benchmarks: BenchmarkRollup | null;
  // True when the BE recomputed the benchmark rollup at GET time and
  // the new rollup differs structurally from the one persisted with
  // the assessment (new cohort qualified, materially different
  // percentiles, etc.). FE uses it to show a "Cohorts refreshed since
  // this analysis was written - re-run to refresh the narrative"
  // banner so the operator knows the cohort table is live but the
  // LLM explanation may be stale.
  benchmarks_recomputed_from_persisted?: boolean;
  epc_followup_questions: FollowupQuestion[];
  cost_risks: CostRisk[];
  cost_opportunities: CostOpportunity[];
  alignment_items: AlignmentItem[];
  // Per-finding dismissal map keyed by "<kind>:<index>" - e.g.
  // "risk:2" dismisses cost_risks[2]. Filled by the operator via
  // POST /projects/procurement/finding-dismissal. Each entry carries
  // who dismissed it and when so the FE can render a "Dismissed by X
  // on Y - Reinstate" affordance. Dismissed findings are filtered out
  // of the EPC summary text, the xlsx export, and the worst/best-case
  // bracket. Reinstating clears the entry.
  dismissals?: Record<string, { by: string; at: string }>;
  // ``Hide from EPC summary`` - the softer toggle Noaa introduced
  // 2026-06-09. Same shape + keying as ``dismissals``. Hidden findings
  // are filtered out of the EPC summary text + xlsx ONLY; they stay
  // visible on the platform assessment (no struck-through tombstone),
  // and they still count toward the worst/best-case bracket. Reinstate
  // by re-clicking (the FE sends ``dismissed: false`` with
  // ``scope: "epc_only"``).
  epc_hidden?: Record<string, { by: string; at: string }>;
}

export interface ProcurementFindingDismissalRequest {
  assessment_id: string;
  kind: "alignment" | "followup" | "risk" | "opportunity";
  index: number;
  dismissed: boolean;
  // ``dismiss`` (default) is the existing behaviour - tombstones the
  // finding on-screen + filters from EPC text/xlsx + filters from the
  // worst/best-case bracket. ``epc_only`` is the softer toggle: keep
  // the finding visible on the platform, filter from EPC text/xlsx
  // only. Same toggle semantics in both cases.
  scope?: "dismiss" | "epc_only";
  // FE-resolved actor name (from the Kinde user object) so the BE
  // can stamp ``Dismissed by <noaa@inrange.io>`` rather than falling
  // back to the opaque ``kp_<hex>`` Kinde sub when the JWT doesn't
  // carry an ``email`` claim. Mirrors the ``pulled_by_name`` pattern
  // used by Gmail / Drive upload imports.
  actor_name?: string;
}

// The status discriminator the BE returns from /projects/procurement/get.
// "not_assessable": no assessment yet, and the inputs aren't both ready.
// "not_run": both inputs ready, no assessment row yet.
// "stale": persisted assessment exists but at least one source has
//   been replaced, removed or changed since.
// "fresh": persisted assessment is up-to-date.
export type ProcurementStatus =
  | "not_assessable"
  | "not_run"
  | "stale"
  | "fresh"
  | "dry_run";

export type ProcurementInputKind = "epc_cost" | "on_site_report";
// Mirrors the BE ``agents.procurement.inputs.InputState``. Run is refused
// unless both kinds are "ready".
export type ProcurementInputState = "ready" | "missing" | "extracting" | "unreadable";
export type ProcurementInputs = Record<ProcurementInputKind, ProcurementInputState>;

export interface ProcurementInputOption {
  upload_id: string;
  original_name: string;
  content_type: string | null;
  has_extracted_text: boolean;
  // The site the wrong-site check suspects this file is about instead.
  other_site: string | null;
  created_at: string;
  state: ProcurementInputState;
}

// One input's candidates, newest first, and the upload the assessment reads:
// the operator's choice when ``chosen``, else the automatic pick.
export interface ProcurementInputChoice {
  selected_upload_id: string | null;
  // Where the selected file was seen, and when, in the Files tab's shape.
  selected_sightings: Sighting[];
  chosen: boolean;
  // The chosen upload was removed or excluded, so the automatic pick is used.
  chosen_unavailable: boolean;
  options: ProcurementInputOption[];
}

export interface ChooseProcurementInputRequest {
  project_id: string;
  kind: ProcurementInputKind;
  // null goes back to the automatic pick.
  upload_id: string | null;
}

export const PROCUREMENT_INPUT_LABEL: Record<ProcurementInputKind, string> = {
  epc_cost: "EPC cost spreadsheet",
  on_site_report: "On-site survey",
};

const INPUT_STATE_LABEL: Record<ProcurementInputState, string> = {
  ready: "ready",
  missing: "not uploaded",
  extracting: "still being read",
  unreadable: "the assessment can't read it",
};

export const describeProcurementInput = (
  kind: ProcurementInputKind,
  state: ProcurementInputState,
): string => `${PROCUREMENT_INPUT_LABEL[kind]}: ${INPUT_STATE_LABEL[state]}`;

export const procurementInputsReady = (inputs: ProcurementInputs): boolean =>
  Object.values(inputs).every((state) => state === "ready");

// The cross-site compare route names an unready input as "<kind>_<state>".
export const describeProcurementInputReason = (reason: string): string | null => {
  for (const kind of Object.keys(PROCUREMENT_INPUT_LABEL) as ProcurementInputKind[]) {
    const state = reason.slice(kind.length + 1);
    if (reason.startsWith(`${kind}_`) && state in INPUT_STATE_LABEL) {
      return describeProcurementInput(kind, state as ProcurementInputState);
    }
  }
  return null;
};

// One row of the EPC cost build-up - the per-row 3-value shape the
// procurement tab renders as a per-row card.
export interface EpcCostRow {
  section: string | null;
  label: string;
  total_cost: number | null;
  per_kwp: number | null;
  comment: string | null;
}

interface ProcurementAssessmentBase {
  project_id: string;
  status: ProcurementStatus;
  // The project's currency (``GBP`` / ``EUR``), pulled from the linked
  // BMA site. Drives the currency symbol on every money value the tab
  // renders (UK sites are £, Netherlands sites are €). ``null`` until
  // the site is linked + refreshed; the tab defaults to £ when absent.
  currency_code: string | null;
  // The procurement findings LRO that's currently in flight for
  // this project, if any. Set by the BE when an auto-trigger or a
  // manual Run is mid-run; null otherwise. The FE picks this up
  // on mount and resumes polling (via useAsyncOperation) so a tab
  // navigation away/back doesn't drop the running-state UI.
  pending_operation_key?: string | null;
  inputs: ProcurementInputs;
  input_choices: Record<ProcurementInputKind, ProcurementInputChoice>;
}

export interface ProcurementNotAssessable extends ProcurementAssessmentBase {
  status: "not_assessable";
}

export interface ProcurementNotRun extends ProcurementAssessmentBase {
  status: "not_run";
  epc_cost_upload_id: string;
  on_site_report_upload_id: string;
}

export interface ProcurementAssessmentPayload extends ProcurementAssessmentBase {
  status: "fresh" | "stale";
  assessment_id: string;
  created_at: string;
  epc_cost_upload_id: string;
  epc_cost_input_version: string | null;
  on_site_report_upload_id: string;
  on_site_report_input_version: string | null;
  findings: ProcurementFindings;
  epc_cost_rows: EpcCostRow[];
  epc_extra_rows: EpcCostRow[];
  model_id: string | null;
  usage: {
    input_tokens: number | null;
    output_tokens: number | null;
  };
  stale_reason?: string;
  // Whether a persisted AI trace exists for this assessment. Null/false
  // for assessments produced before trace capture -> the "View AI trace"
  // button is hidden. Mirrors the BE ``has_ai_trace`` column.
  has_ai_trace?: boolean | null;
}

export type ProcurementGetResult =
  | ProcurementNotAssessable
  | ProcurementNotRun
  | ProcurementAssessmentPayload;
